import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Grid,
  Pagination,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  AlternateEmail as AlternateEmailIcon,
  Search as SearchIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  FilterList as FilterListIcon,
  DateRange as DateRangeIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  BarChart as BarChartIcon,
  RequestPage as RequestPageIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import useStore from "../../store";
import { colors, routes } from "../../constants";
import SimpleDialog from "../../components/SimpleDialog";
import ResponsesModal from "../../components/ResponsesModal";
import PreliminaryResultsModal from "../../components/PreliminaryResultsModal";
import api from "../../api";

export function SurveyList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const {
    surveys: storeSurveys = [],
    currentPage,
    surveysPerPage,
    totalItems,
    setSurveys,
    setCurrentPage,
    setSurveysPerPage,
    setTotalItems,
  } = useStore();

  const [dialog, setDialog] = useState({ open: false, id: null, title: "" });
  const [lockDialog, setLockDialog] = useState({
    open: false,
    id: null,
    title: "",
    content: "",
    currentLockStatus: false,
  });
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, id: null, title: "" });
  const [responsesModal, setResponsesModal] = useState({ open: false, surveyId: null });
  const [preliminaryResultsModal, setPreliminaryResultsModal] = useState({ open: false, surveyId: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const surveys = storeSurveys.length > 0 ? storeSurveys : [];

  useEffect(() => {
    document.body.style.backgroundColor = "#f5f5f5";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  // Fetch surveys from the backend
  useEffect(() => {
    const fetchSurveys = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getSurveys({
          page: currentPage,
          pageSize: surveysPerPage,
          searchQuery,
        });
        setTotalItems(data.totalItems);
        setSurveys(data.surveys);

        console.log("Total Items:", data.totalItems);

        // Validate currentPage
        const maxPage = Math.ceil(data.totalItems / surveysPerPage);
        console.log("maxPage:", maxPage);
        if (currentPage > maxPage && maxPage > 0) {
          setCurrentPage(maxPage);
        } else if (currentPage < 1) {
          setCurrentPage(1);
        }
      } catch (err) {
        setError(err.message);
        enqueueSnackbar(err.message, { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchSurveys();
  }, [currentPage, surveysPerPage, searchQuery, setSurveys, setTotalItems, setCurrentPage, enqueueSnackbar]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Filter surveys based on search query and status
  const filteredSurveys = surveys.filter((survey) => {
    const matchesSearch = survey.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      survey.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || survey.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });


  // Handle delete survey (mock action for now; replace with API call later)
  const handleDelete = async () => {
    try {
      const response = await api.deleteSurvey(dialog.id);
      const updatedSurveys = surveys.filter((survey) => survey.id !== dialog.id);
      setSurveys(updatedSurveys);
      setTotalItems(updatedSurveys.length);
      enqueueSnackbar(response.message || formatMessage({ id: "survey.delete_success", defaultMessage: "Assessment deleted successfully" }), {
        variant: response.success === true ? "success" : "error",
      });
      setDialog({ open: false, id: null, title: "" });
    } catch (error) {
      enqueueSnackbar(error.message || formatMessage({ id: "survey.delete_failed", defaultMessage: "Failed to delete assessment" }), {
        variant: "error",
      });
      console.error("Delete survey error:", error);
    }
  };

  // Handle duplicate survey
  const handleDuplicate = async (id) => {
    try {
      const response = await api.duplicateSurvey(id);
      const surveyToDuplicate = surveys.find((survey) => survey.id === id);
      const newSurvey = {
        ...surveyToDuplicate,
        id: response.surveyId,
        title: response.title,
        emailsSent: 0,
        surveysOpened: 0,
        surveysSubmitted: 0,
      };
      const updatedSurveys = [...surveys, newSurvey];
      setSurveys(updatedSurveys);
      setTotalItems(updatedSurveys.length);
      enqueueSnackbar(response.message || formatMessage({ id: "survey.duplicate_success", defaultMessage: "Assessment duplicated successfully" }), {
        variant: response.success === true ? "success" : "error",
      });
      setDuplicateDialog({ open: false, id: null, title: "" });
    } catch (error) {
      enqueueSnackbar(error.message || formatMessage({ id: "survey.duplicate_failed", defaultMessage: "Failed to duplicate assessment" }), {
        variant: "error",
      });
      console.error("Duplicate survey error:", error);
    }
  };

  // Open duplicate confirmation dialog
  const openDuplicateDialog = (survey) => {
    setDuplicateDialog({ open: true, id: survey.id, title: survey.title });
  };

  // Handle lock/unlock survey
  const handleLockToggle = async (surveyId, currentLockStatus) => {
    try {
      const newLockStatus = !currentLockStatus;
      const response = await api.lockSurvey(surveyId, newLockStatus);

      // Update the survey in the local state
      const updatedSurveys = surveys.map((survey) =>
        survey.id === surveyId ? { ...survey, released: newLockStatus, prevStatus: survey.status, status: newLockStatus ? "closed" : (survey.prevStatus || "draft") } : survey
      );
      setSurveys(updatedSurveys);

      enqueueSnackbar(
        newLockStatus
          ? formatMessage({ id: "survey.locked_message", defaultMessage: "Assessment has been locked. Respondents can no longer access it." })
          : formatMessage({ id: "survey.unlocked_message", defaultMessage: "Assessment has been unlocked. Respondents can now access it." }),
        {
          variant: "success",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        }
      );

      console.log(`Survey ${newLockStatus ? 'locked' : 'unlocked'} successfully:`, response);
    } catch (error) {
      enqueueSnackbar(
        currentLockStatus
          ? formatMessage({ id: "survey.unlock_failed", defaultMessage: "Failed to unlock assessment: {error}" }, { error: error.message })
          : formatMessage({ id: "survey.lock_failed", defaultMessage: "Failed to lock assessment: {error}" }, { error: error.message }),
        {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        }
      );
      console.error(`Failed to ${currentLockStatus ? 'unlock' : 'lock'} survey:`, error);
    }
  };

  const openConfirmLockDialog = (survey) => {
    const isCurrentlyLocked = !!survey.released;
    if (isCurrentlyLocked) {
      enqueueSnackbar(formatMessage({ id: "survey.unlock_survey" }), { variant: "error" });
      return;
    }
    const actionTitle = formatMessage({ id: "survey.lock_survey" });
    const message = formatMessage({ id: "survey.unlock_survey_confirm" });
    setLockDialog({
      open: true,
      id: survey.id,
      title: actionTitle,
      content: message,
      currentLockStatus: false,
    });
  };

  const handleAddNewSurvey = () => {
    console.log("Navigating to new survey page:", `${routes.surveys}/new`);
    navigate(`${routes.surveys}/new`);
  };

  const handleOpenResponsesModal = (surveyId) => {
    setResponsesModal({ open: true, surveyId });
  };

  const handleCloseResponsesModal = () => {
    setResponsesModal({ open: false, surveyId: null });
  };

  const handleOpenPreliminaryResultsModal = (surveyId) => {
    setPreliminaryResultsModal({ open: true, surveyId });
  };

  const handleClosePreliminaryResultsModal = () => {
    setPreliminaryResultsModal({ open: false, surveyId: null });
  };

  const totalPages = Math.ceil(totalItems / surveysPerPage);

  // Helper function to get status styling
  const getStatusStyling = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return {
          color: '#4caf50',
          bgColor: 'rgba(76, 175, 80, 0.1)',
          borderColor: '#4caf50'
        };
      case 'closed':
        return {
          color: '#f44336',
          bgColor: 'rgba(244, 67, 54, 0.1)',
          borderColor: '#f44336'
        };
      default:
        return {
          color: '#757575',
          bgColor: 'rgba(117, 117, 117, 0.1)',
          borderColor: '#757575'
        };
    }
  };

  // Helper function to calculate response rate
  const getResponseRate = (survey) => {
    let sent = 0;
    if (survey.is_publicly_available) {
      sent = survey.surveysOpened || 0;
    } else {
      sent = survey.emailsSent || 0;
    }
    const submitted = survey.surveysSubmitted || 0;
    return sent > 0 ? Math.round((submitted / sent) * 100) : 0;
  };


  console.log("totalPages:", totalPages);
  console.log("currentPage:", currentPage);

  return (
    <Box
      sx={{
        maxWidth: "1500px",
        margin: "0 auto",
        padding: { xs: "16px", sm: "24px", md: "32px" },
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        pt: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: { xs: 4, md: 3 },
          gap: { xs: 2, sm: 0 },
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, width: "100%", alignItems: "stretch" }}>
          <TextField
            variant="outlined"
            placeholder={formatMessage({ id: "survey.search_placeholder" })}
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
            }}
            sx={{
              flex: 1,
              maxWidth: { xs: "100%", sm: "400px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#f9f9f9",
                fontSize: { xs: "0.9rem", sm: "1rem" },
                "&:hover fieldset": {
                  borderColor: colors.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.main,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1.5, sm: 2 }, width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<span>+</span>}
            onClick={handleAddNewSurvey}
            sx={{
              borderRadius: "24px",
              textTransform: "none",
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              fontSize: { xs: "0.9rem", sm: "1rem" },
              whiteSpace: "nowrap",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <FormattedMessage id="survey.add_new" />
          </Button>
        </Box>
      </Box>

      {/* Loading and Error States */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 8, flexGrow: 1 }}>
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            <FormattedMessage id="survey.loading_surveys" defaultMessage="Loading assessments..." />
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", py: 8, flexGrow: 1 }}>
          <Typography variant="h6" sx={{ color: "error.main", mb: 2 }}>
            {error}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setCurrentPage(1)}
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            <FormattedMessage id="survey.retry" defaultMessage="Retry" />
          </Button>
        </Box>
      ) : surveys.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, flexGrow: 1 }}>
          <Typography variant="h6" sx={{ color: "text.secondary", mb: 2 }}>
            {searchQuery
              ? <FormattedMessage id="survey.no_surveys_match_search" defaultMessage="No assessments match your search." />
              : <FormattedMessage id="survey.no_surveys_found" defaultMessage="No assessments found. Create one to get started!" />}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(`${routes.surveys}/new`)}
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            <FormattedMessage id="survey.create_survey" defaultMessage="Create a Assessment" />
          </Button>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, overflowY: "hidden" }}>
          {/* Filter Summary */}
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 2,
            backgroundColor: "rgba(0, 67, 142, 0.05)",
            borderRadius: "12px",
            border: "1px solid rgba(0, 67, 142, 0.1)"
          }}>
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              <FormattedMessage
                id="survey.showing_surveys"
                values={{ filtered: filteredSurveys.length, total: surveys.length }}
              />
              {statusFilter !== "all" && (
                <>
                  {" • "}
                  <FormattedMessage
                    id="survey.filtered_by"
                    values={{ status: statusFilter }}
                  />
                </>
              )}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              {["active", "closed", "draft"].map(status => {
                const count = surveys.filter(s => s.status?.toLowerCase() === status).length;
                if (count === 0) return null;
                const statusStyle = getStatusStyling(status);
                const statusLabelKey = `survey.status_${status}_label`;
                const statusLabel = status === "active"
                  ? formatMessage({ id: statusLabelKey, defaultMessage: "Active: {count}" }, { count })
                  : status === "closed"
                    ? formatMessage({ id: statusLabelKey, defaultMessage: "Closed: {count}" }, { count })
                    : formatMessage({ id: statusLabelKey, defaultMessage: "Draft: {count}" }, { count });
                return (
                  <Chip
                    key={status}
                    label={statusLabel}
                    size="small"
                    sx={{
                      backgroundColor: statusStyle.bgColor,
                      color: statusStyle.color,
                      fontSize: "0.75rem"
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          <Grid container spacing={2}>
            {filteredSurveys.map((survey) => {
              const statusStyle = getStatusStyling(survey.status);
              const responseRate = getResponseRate(survey);
              // Use status color for response rate to match survey status
              const responseRateStyle = {
                color: statusStyle.color,
                bgColor: statusStyle.bgColor
              };

              return (
                <Grid item xs={12} sm={6} lg={4} key={survey.id}>
                  <Card
                    sx={{
                      borderRadius: "12px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      border: `1px solid ${statusStyle.borderColor}`,
                      backgroundColor: "#fff",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                      },
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      // minHeight: "280px",
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: colors.main,
                            lineHeight: 1.2,
                            flex: 1,
                            pr: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {survey.title}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flexShrink: 0 }}>
                          <Chip
                            label={survey.status?.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: statusStyle.bgColor,
                              color: statusStyle.color,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: "20px",
                            }}
                          />
                          {survey.respond_mode === "voice" && (
                            <Chip
                              icon={<RecordVoiceOverIcon sx={{ fontSize: "0.8rem !important" }} />}
                              label={<FormattedMessage id="survey.voice_badge" defaultMessage="Modern" />}
                              size="small"
                              data-testid="voice-badge"
                              sx={{
                                backgroundColor: "rgba(0, 67, 142, 0.08)",
                                color: colors.main,
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                height: "20px",
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Description */}

                      {/* Metrics and Progress Bar in One Row */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
                        {/* Metrics */}
                        <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
                          {!survey.is_publicly_available && <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.orange, fontSize: "1rem" }}>
                              {survey.emailsSent || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              <FormattedMessage id="survey.emails" />
                            </Typography>
                          </Box>}
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.main, fontSize: "1rem" }}>
                              {survey.surveysOpened || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              <FormattedMessage id="survey.views" />
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.green, fontSize: "1rem" }}>
                              {survey.surveysSubmitted || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              <FormattedMessage id="survey.responses" />
                            </Typography>
                          </Box>
                        </Box>

                        {/* Response Rate Progress Bar - Takes remaining space */}
                        <Box sx={{ flex: 1, minWidth: "120px" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary" }}>
                              <FormattedMessage id="survey.response_rate" />
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: responseRateStyle.color,
                                backgroundColor: responseRateStyle.bgColor,
                                px: 1,
                                borderRadius: "6px",
                                fontSize: "0.7rem"
                              }}
                            >
                              {responseRate}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={responseRate}
                            sx={{
                              height: "4px",
                              borderRadius: "2px",
                              backgroundColor: "rgba(0,0,0,0.08)",
                              width: "100%",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: statusStyle.color,
                                borderRadius: "2px",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContent>

                    <Divider sx={{ mb: 2 }} />

                    {/* Actions */}
                    <Box sx={{ p: 2, pt: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Tooltip title={<FormattedMessage id="survey.edit_survey" />}>
                          <IconButton
                            onClick={() => navigate(`${routes.surveys}/${survey.id}`)}
                            disabled={survey.released || survey.status === "active"}
                            size="small"
                            sx={{
                              color: "primary.main",
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                            }}
                          >
                            <EditIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={<FormattedMessage id="survey.delete_survey" />}>
                          <IconButton
                            onClick={() =>
                              setDialog({ open: true, id: survey.id, title: survey.title })
                            }
                            size="small"
                            sx={{
                              color: "error.main",
                              "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.04)" },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={<FormattedMessage id="survey.duplicate_survey" />}>
                          <IconButton
                            onClick={() => openDuplicateDialog(survey)}
                            size="small"
                            sx={{
                              color: "secondary.main",
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={<FormattedMessage id="survey.view_collectors" />}>
                          <IconButton
                            onClick={() => navigate(`${routes.collector}/${survey.id}`, { state: { surveyStatus: survey.status } })}
                            size="small"
                            sx={{
                              color: "info.main",
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                            }}
                          >
                            <AlternateEmailIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={survey.released ? <FormattedMessage id="survey.unlock_survey" /> : <FormattedMessage id="survey.lock_survey" />}>
                          <IconButton
                            onClick={() => openConfirmLockDialog(survey)}
                            size="small"
                            sx={{
                              color: survey.released ? "warning.main" : "success.main",
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                            }}
                          >
                            {survey.released ? <LockIcon sx={{ fontSize: "1rem" }} /> : <LockOpenIcon sx={{ fontSize: "1rem" }} />}
                          </IconButton>
                        </Tooltip>
                        {survey.status === "active" &&
                          <Tooltip title={<FormattedMessage id="survey.preliminary_results" defaultMessage="Preliminary results" />}>
                            <IconButton
                              onClick={() => handleOpenPreliminaryResultsModal(survey.id)}
                              size="small"
                              disabled={survey.status === "closed" || survey.status === "draft"}
                              sx={{
                                color: "rgb(231 203 48)",
                                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                              }}
                            >
                              <RequestPageIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>}
                      </Box>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                        {survey.created_at
                          ? new Date(survey.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                          : '—'}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 6,
              flexShrink: 1,
              pb: 2,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                },
              }}
            />
          </Box>
        </Box>
      )}

      <SimpleDialog
        open={dialog.open}
        title={<FormattedMessage id="survey.delete_survey" />}
        content={
          <FormattedMessage
            id="survey.confirm_delete"
            values={{ title: dialog.title }}
          />
        }
        onConfirm={handleDelete}
        setOpen={(open) => setDialog({ ...dialog, open })}
      />

      <SimpleDialog
        open={lockDialog.open}
        title={lockDialog.title}
        content={lockDialog.content}
        onConfirm={() => {
          handleLockToggle(lockDialog.id, lockDialog.currentLockStatus);
          setLockDialog({ ...lockDialog, open: false });
        }}
        setOpen={(open) => setLockDialog({ ...lockDialog, open })}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#ffebee',
            border: '2px solid #f44336',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)',
          },
          '& .MuiDialogTitle-root': {
            color: '#5d4037',
            fontWeight: 'bold',
            fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
            borderBottom: '1px solid rgba(244, 67, 54, 0.2)',
            paddingBottom: '12px',
          },
          '& .MuiDialogContentText-root': {
            color: '#5d4037',
            fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          },
          '& .MuiButton-root': {
            fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif',
            fontWeight: 'medium',
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
            },
          },
        }}
      />

      <SimpleDialog
        open={duplicateDialog.open}
        title={<FormattedMessage id="survey.duplicate_survey" />}
        content={formatMessage(
          { id: "survey.confirm_duplicate", defaultMessage: "This will create a new assessment with the same questions and theme." },
          { title: duplicateDialog.title }
        )}
        onConfirm={() => handleDuplicate(duplicateDialog.id)}
        setOpen={(open) => setDuplicateDialog({ ...duplicateDialog, open })}
      />

      <ResponsesModal
        open={responsesModal.open}
        onClose={handleCloseResponsesModal}
        surveyId={responsesModal.surveyId}
        title={formatMessage({ id: "survey.survey_responses", defaultMessage: "Assessment Responses" })}
        loadingMessage={formatMessage({ id: "survey.loading_responses", defaultMessage: "Loading assessment responses..." })}
      />

      <PreliminaryResultsModal
        open={preliminaryResultsModal.open}
        onClose={handleClosePreliminaryResultsModal}
        surveyId={preliminaryResultsModal.surveyId}
        title={formatMessage({ id: "survey.preliminary_results_title", defaultMessage: "Preliminary Results" })}
        loadingMessage={formatMessage({ id: "survey.loading_preliminary_results", defaultMessage: "Loading preliminary results..." })}
      />

    </Box>
  );
}

export default SurveyList;
